import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Eye, MoreHorizontal, Pause, Play, Search, Trash2, X } from 'lucide-react';

import { useTranslation } from '@/utils/i18n';
import { useAuthStore } from '@/stores/AuthState';
import { useUiSignals } from '@/stores/uiSignals';
import { HOME_ROUTE } from '@/constants/routes';
import { useMyRecommendations, useDeleteRecommendation } from '@/hooks/queries/myReco';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { useDismissKeyboardOnDrag } from '@/hooks/useDismissKeyboardOnDrag';
import PullToRefreshSpinner from '@/components/PullToRefreshSpinner';
import { getPreviewUrl, useAudioPlayer } from '@/stores/audioPlayer';
import { updateTrackWithDeezer } from '@/api/track';
import { getTrackImageUrl } from '@/utils/imageHelpers';
import { toast } from '@/stores/toast';
import AvatarChain from '@/components/AvatarChain';
import ReboundChainsSheet from '@/components/ReboundChainsSheet';
import ConfirmSheet from '@/components/ConfirmSheet';
import ImpactTotals from '@/components/ImpactTotals';
import ReactionsSheet from '@/components/ReactionsSheet';
import UsersSheet from '@/components/UsersSheet';
import ServiceLogo from '@/components/ServiceLogo';
import PlaybackProgress from '@/components/PlaybackProgress';
import { cn } from '@/lib/utils';
import type { MyRecommendation } from '@/types';
import TabBarSpacer from '@/components/navigation/TabBarSpacer';

const RACORE_OFFICIAL_SLUG = 'racore-official';

/**
 * "Impact de mes recommandations" (wireframe 4). Lists the user's own recos
 * with their reach (people who discovered the song), reactions, and streaming
 * openings. Empty state = 4b, delete = 4c. All data from GET /recommendation/mine.
 */
export const Route = createFileRoute('/_tabs/my-reco')({
  component: MyRecoScreen,
});

function MyRecoScreen() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const query = useDebounced(search.trim(), 300);

  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useMyRecommendations(query);

  const { pullY, refreshing, handlers } = usePullToRefresh(() => refetch());
  const searchRef = useRef<HTMLInputElement>(null);
  const keyboard = useDismissKeyboardOnDrag(searchRef);

  const recos = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);

  const [toDelete, setToDelete] = useState<MyRecommendation | null>(null);
  const del = useDeleteRecommendation(query);

  const confirmDelete = () => {
    if (!toDelete) return;
    del.mutate(toDelete.documentId, {
      onSuccess: () => {
        toast.success(t('myReco.delete.success'));
        setToDelete(null);
      },
      onError: () => toast.error(t('myReco.delete.error')),
    });
  };

  const isSearching = query.length > 0;

  return (
    <div
      className="tw:relative tw:flex tw:h-full tw:flex-col tw:overflow-y-auto tw:px-5 tw:pb-8"
      data-testid="screen-my-reco"
      {...handlers}
      // search-scroll-dismiss-keyboard — the same touches also close the
      // keyboard after a search; the pull keeps refreshing.
      onTouchStart={(e) => {
        handlers.onTouchStart(e);
        keyboard.onTouchStart(e);
      }}
      onTouchMove={(e) => {
        handlers.onTouchMove(e);
        keyboard.onTouchMove(e);
      }}
      onScroll={(e) => {
        const el = e.currentTarget;
        if (
          hasNextPage &&
          !isFetchingNextPage &&
          el.scrollTop + el.clientHeight >= el.scrollHeight - 120
        ) {
          void fetchNextPage();
        }
      }}
    >
      <PullToRefreshSpinner pullY={pullY} refreshing={refreshing} />
      <h1 className="tw:py-5 tw:heading-2 tw:text-white">{t('myReco.title')}</h1>

      {/* B3+B4 — global impact counters (server-side totals, not page sums). */}
      <ImpactTotals totals={data?.pages[0]?.meta.totals} />

      {/* Search */}
      <div className="tw:relative tw:mb-4">
        <Search
          size={18}
          className="tw:pointer-events-none tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-grey-4"
        />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('myReco.searchPlaceholder')}
          data-testid="input-myreco-search"
          className="tw:h-11 tw:w-full tw:rounded-full tw:bg-dark-1 tw:pl-10 tw:pr-10 tw:text-[15px] tw:text-white tw:placeholder:text-grey-4 tw:focus:outline-none"
        />
        {search && (
          <button
            type="button"
            aria-label="Clear"
            onClick={() => setSearch('')}
            className="tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-grey-4"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="tw:flex tw:flex-1 tw:items-center tw:justify-center">
          <span className="tw:size-7 tw:animate-spin tw:rounded-full tw:border-2 tw:border-white/20 tw:border-t-blue-primary" />
        </div>
      ) : isError ? (
        <div className="tw:flex tw:flex-1 tw:items-center tw:justify-center">
          <p className="tw:text-center tw:text-base tw:text-grey-2">{t('common.error.generic')}</p>
        </div>
      ) : recos.length === 0 ? (
        isSearching ? (
          <p className="tw:mt-10 tw:text-center tw:text-base tw:text-grey-2">
            {t('myReco.noResults')}
          </p>
        ) : (
          <EmptyState text={t('myReco.empty.text')} />
        )
      ) : (
        <>
          <div className="tw:flex tw:flex-col tw:gap-4">
            {recos.map((reco) => (
              <ImpactCard key={reco.id} reco={reco} onDelete={() => setToDelete(reco)} />
            ))}
          </div>
          {isFetchingNextPage && (
            <div className="tw:flex tw:justify-center tw:py-4">
              <span className="tw:size-6 tw:animate-spin tw:rounded-full tw:border-2 tw:border-white/20 tw:border-t-blue-primary" />
            </div>
          )}
        </>
      )}

      <ConfirmSheet
        open={toDelete !== null}
        title={t('myReco.delete.title')}
        cancelLabel={t('myReco.delete.cancel')}
        confirmLabel={t('myReco.delete.confirm')}
        loading={del.isPending}
        onConfirm={confirmDelete}
        onClose={() => (del.isPending ? undefined : setToDelete(null))}
      />
      <TabBarSpacer />
    </div>
  );
}

/** Format a recommendation's timestamp as a localized date (e.g. "12 juin 2026"). */
function formatRecoDate(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/** Debounce a rapidly-changing value (search box) to avoid a request per keystroke. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function ImpactCard({ reco, onDelete }: { reco: MyRecommendation; onDelete: () => void }) {
  const { t, language } = useTranslation();
  const me = useAuthStore((s) => s.userInfo);
  const { toggle, currentId, isPlaying } = useAudioPlayer();
  const navigate = useNavigate();
  const [usersOpen, setUsersOpen] = useState(false);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [reboundsOpen, setReboundsOpen] = useState(false);
  /** Emoji whose tab the reactions sheet should open on (null = "All"). */
  const [reactionsEmoji, setReactionsEmoji] = useState<string | null>(null);

  const openReactions = (emoji: string | null) => {
    setReactionsEmoji(emoji);
    setReactionsOpen(true);
  };

  /**
   * Jump to this song's card in the Home deck. The reco lives in the feed of
   * the circle it was shared with, so we select that circle first and let Home
   * page forward until the card is mounted.
   */
  const openSongCard = () => {
    // comment-circle-scope — atterrir là où la conversation PEUT exister. Sur
    // Racore Officiel il n'y en a pas (le back refuse d'y répondre), et Racore
    // est souvent premier dans `groups` : c'est le cercle par défaut au partage.
    // Repli sur le premier tout court quand la reco n'est que publique.
    const conversationCircle = reco.groups.find((g) => g.slug !== RACORE_OFFICIAL_SLUG);
    const circleId = (conversationCircle ?? reco.groups[0])?.id ?? null;
    const signals = useUiSignals.getState();
    if (circleId != null) signals.setLandOnCircle(circleId);
    signals.setLandOnReco(reco.id);
    void navigate({ to: HOME_ROUTE });
  };

  const dateStamp = formatRecoDate(reco.createdAt, language);

  const track = reco.track;
  const image = getTrackImageUrl(track);
  const preview = getPreviewUrl(track);
  const audioId = `myreco-${reco.id}`;
  const playing = currentId === audioId && isPlaying;
  const refresh = () => updateTrackWithDeezer(track.isrc);

  const artists = (track.artists ?? [])
    .map((a) => a.name)
    .filter(Boolean)
    .join(', ');

  // Reactions & openings, most-frequent first.
  const reactionEntries = (Object.entries(reco.reactions.counts) as [string, number][])
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);

  const openingServices = (Object.entries(reco.openings.byService) as [string, number][])
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <section
      className="tw:rounded-2xl tw:bg-dark-1/60 tw:p-4"
      data-testid={`impact-card-${reco.id}`}
    >
      {/* Header: artwork + title/artist + delete */}
      <div className="tw:flex tw:items-start tw:gap-3">
        {/* playback-progress-ring — the cover clips its overflow, so the outline is
            its sibling, drawn 4 px around it. */}
        <div className="tw:relative tw:size-16 tw:shrink-0">
          <div className="tw:relative tw:size-full tw:overflow-hidden tw:rounded-xl tw:bg-dark-primary">
            {image && <img src={image} alt="" className="tw:size-full tw:object-cover" />}
            <button
              type="button"
              onClick={() =>
                toggle(preview, audioId, refresh, {
                  title: track.title,
                  artist: artists,
                  artwork: image,
                })
              }
              disabled={!preview}
              aria-label={playing ? 'Pause' : 'Play'}
              data-testid={`btn-impact-play-${reco.id}`}
              className={cn(
                'tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:bg-black/40 tw:text-white',
                !preview && 'tw:opacity-40',
              )}
            >
              {playing ? (
                <Pause size={18} fill="currentColor" />
              ) : (
                <Play size={18} fill="currentColor" />
              )}
            </button>
          </div>
          <PlaybackProgress audioId={audioId} shape="rounded" size={64} radius={12} />
        </div>

        <div className="tw:min-w-0 tw:flex-1">
          {/* Title + artist open the song card itself (the Home deck card for
              this very reco), so the user can play it, read the comment and
              react without hunting for it in the feed. */}
          <button
            type="button"
            onClick={openSongCard}
            data-testid={`btn-impact-open-card-${reco.id}`}
            className="tw:block tw:w-full tw:text-left"
          >
            <p className="tw:truncate tw:text-base tw:font-semibold tw:text-white">{track.title}</p>
            <p className="tw:truncate tw:caption tw:text-grey-3">{artists}</p>
          </button>
          <p
            className="tw:mt-0.5 tw:text-[11px] tw:text-grey-4"
            data-testid={`impact-date-${reco.id}`}
          >
            {dateStamp}
          </p>
          {reco.groups.length > 0 && (
            <div
              className="tw:mt-1.5 tw:flex tw:flex-wrap tw:gap-1"
              data-testid={`impact-groups-${reco.id}`}
            >
              {reco.groups.map((g) => (
                <span
                  key={g.id}
                  className="tw:flex tw:items-center tw:gap-1 tw:rounded-full tw:bg-dark-primary/70 tw:px-2 tw:py-0.5 tw:text-[11px] tw:text-grey-2"
                >
                  {g.emoji && <span>{g.emoji}</span>}
                  <span className="tw:max-w-24 tw:truncate">
                    {g.slug === RACORE_OFFICIAL_SLUG ? 'Racore' : g.name}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onDelete}
          aria-label={t('myReco.delete.confirm')}
          data-testid={`btn-impact-delete-${reco.id}`}
          className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-dark-primary/70 tw:text-error tw:transition-opacity tw:active:opacity-70"
        >
          <Trash2 size={18} />
        </button>
      </div>

      {/* Views */}
      <div
        className="tw:mt-4 tw:flex tw:items-center tw:gap-1.5 tw:text-grey-3"
        data-testid={`impact-views-${reco.id}`}
      >
        <Eye size={15} />
        <span className="tw:text-[13px]">{t('myReco.views', { count: reco.viewsCount })}</span>
      </div>

      {/* Reach */}
      <div className="tw:mt-2 tw:flex tw:items-center tw:justify-between tw:gap-3">
        <p className="tw:flex-1 tw:text-sm tw:text-grey-2">{t('myReco.reachLabel')}</p>
        <span
          className="tw:text-2xl tw:font-bold tw:text-white"
          data-testid={`impact-reach-${reco.id}`}
        >
          {reco.reachCount}
        </span>
      </div>

      {/* B1-feat + B5 chain — [Moi] → direct discoverers, then "…" → the batch
          that discovered THROUGH rebounds. Tapping a batch opens its sheet. */}
      <div
        className="tw:mt-3 tw:cursor-pointer"
        data-testid={`impact-people-line-${reco.id}`}
        onClick={() => setUsersOpen(true)}
      >
        <AvatarChain
          me={{ avatar_url: me.avatar_url, username: me.username }}
          viewers={reco.viewers}
          reachCount={reco.reachCount}
          meLabel={t('myReco.me')}
          overflowLabel={(n) => t('myReco.andOthers', { count: n })}
          onMore={() => setUsersOpen(true)}
          linkedViewers={(reco.reboundChains ?? [])
            .map((c) => c.rebounder)
            .filter((r) => r != null)}
          // my-reco-rebound-clarity — the node means "this reco was rebounded",
          // so it keys off the REBOUND count. Keying it off viewers made a
          // rebound nobody listened to invisible, while the header counted it.
          linkedCount={reco.rebounds?.count ?? 0}
          onLinkedMore={() => setReboundsOpen(true)}
        />
      </div>

      {/* Reactions + openings — per-emoji and per-service breakdowns (DESC) */}
      {(reactionEntries.length > 0 || openingServices.length > 0) && (
        <div className="tw:mt-4 tw:flex tw:flex-col tw:gap-3 tw:border-t tw:border-white/10 tw:pt-3">
          {reactionEntries.length > 0 && (
            <div
              className="tw:flex tw:items-center tw:gap-2"
              data-testid={`impact-reactions-${reco.id}`}
            >
              <span className="tw:w-20 tw:shrink-0 tw:caption tw:text-grey-3">
                {t('myReco.reactions')}
              </span>
              <div className="tw:flex tw:flex-1 tw:flex-wrap tw:items-center tw:gap-1.5">
                {reactionEntries.slice(0, 6).map(([emoji, count]) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => openReactions(emoji)}
                    aria-label={`${emoji} ${count}`}
                    data-testid={`impact-reaction-chip-${reco.id}-${emoji}`}
                    className="tw:flex tw:items-center tw:gap-1 tw:rounded-full tw:bg-dark-primary/70 tw:px-2 tw:py-0.5 tw:text-[13px] tw:text-white tw:transition-opacity tw:active:opacity-70"
                  >
                    <span className="tw:text-[15px]">{emoji}</span>
                    <span className="tw:font-semibold">{count}</span>
                  </button>
                ))}
                {reactionEntries.length > 6 && (
                  <button
                    type="button"
                    onClick={() => openReactions(null)}
                    aria-label={t('myReco.reactions')}
                    className="tw:flex tw:size-6 tw:items-center tw:justify-center tw:rounded-full tw:bg-dark-primary/70 tw:text-grey-3"
                  >
                    <MoreHorizontal size={15} />
                  </button>
                )}
              </div>
            </div>
          )}

          {openingServices.length > 0 && (
            <div
              className="tw:flex tw:items-center tw:gap-2"
              data-testid={`impact-openings-${reco.id}`}
            >
              <span className="tw:w-20 tw:shrink-0 tw:caption tw:text-grey-3">
                {t('myReco.openings')}
              </span>
              <div className="tw:flex tw:flex-1 tw:flex-wrap tw:items-center tw:gap-2">
                {openingServices.map(([service, count]) => (
                  <span
                    key={service}
                    className="tw:flex tw:items-center tw:gap-1 tw:rounded-full tw:bg-dark-primary/70 tw:px-2 tw:py-0.5 tw:text-[13px] tw:text-white"
                  >
                    <ServiceLogo service={service} size={15} />
                    <span className="tw:font-semibold">{count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <UsersSheet
        open={usersOpen}
        title={t('myReco.reachSheetTitle')}
        viewers={reco.viewers}
        total={reco.reachCount}
        moreLabel={(n) => t('myReco.more', { count: n })}
        onClose={() => setUsersOpen(false)}
      />

      <ReactionsSheet
        open={reactionsOpen}
        title={t('myReco.reactions')}
        entries={reactionEntries}
        users={reco.reactions.users}
        meId={me.id}
        initialEmoji={reactionsEmoji}
        onClose={() => setReactionsOpen(false)}
      />

      {/* my-reco-rebound-clarity — who made the sound travel, with their own
          discoverers indented underneath. Replaces the flat list of
          rebound-discoverers, which never said WHO made it happen. */}
      <ReboundChainsSheet
        open={reboundsOpen}
        title={t('myReco.rebounds.sheetTitle')}
        chains={reco.reboundChains ?? []}
        moreLabel={(n) => t('myReco.more', { count: n })}
        emptyChainLabel={t('myReco.rebounds.noListenerYet')}
        onClose={() => setReboundsOpen(false)}
      />
    </section>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="tw:rounded-2xl tw:bg-dark-1/60 tw:px-6 tw:py-10 tw:text-center">
      <p className="tw:mx-auto tw:max-w-[280px] tw:text-base tw:text-grey-2">{text}</p>
      <img
        src="/images/impact.png"
        alt=""
        aria-hidden="true"
        className="tw:mx-auto tw:mt-8 tw:w-40 tw:max-w-[55%] tw:object-contain"
      />
    </div>
  );
}
